import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import styles from '../styles';
import { getRoles, clearRoles } from '../Actions';
import { sorter, onFilter, filterDropdown, filterIcon, get } from '../utils';
import constants from '../constants';
import DeleteModal from './DeleteModal';
import Breadcrumbs from '../Components/Breadcrumbs';

const RoleList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const [roleDeleteModal, setRoleDeleteModal] = useState(false);
  const [roleListArray, setRoleListArray] = useState([]);
  const [deleteRoleId, setDeleteRoleId] = useState(null);

  const auth = useSelector(state => state.auth);
  const authpermissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const roles = useSelector(state => state.roles);

  useEffect(() => {
    dispatch(getRoles());
    return () => {
      dispatch(clearRoles());
    };
  }, []);

  const showDeleteModal = useCallback(
    roleId => {
      setDeleteRoleId(roleId);
      setRoleListArray(roles.filter(role => role.id !== roleId));
      setRoleDeleteModal(true);
    },
    [deleteRoleId]
  );

  const hideDeleteModal = useCallback(() => {
    setDeleteRoleId(null);
    setRoleListArray([]);
    setRoleDeleteModal(false);
    dispatch(getRoles());
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {authpermissions.includes(constants.permissions.updateRole) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/roles/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {authpermissions.includes(constants.permissions.deleteRole) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => showDeleteModal(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Role',
        dataIndex: 'role',
        sorter: sorter('role'),
        onFilter: onFilter('role'),
        filterDropdown,
        filterIcon,
        width: '15%',
      },
      {
        title: 'Permissions',
        dataIndex: 'permissions',
        sorter: sorter('permissions'),
        onFilter: onFilter('permissions'),
        filterDropdown,
        filterIcon,
        render: permissions =>
          permissions.length ? permissions.join(', ') : 'No Permissions',
      },
    ];

    if (authpermissions.includes(constants.permissions.updateRole)) {
      column.push({
        title: 'Update',
        width: '7%',
        render: updateColumn,
      });
    }

    if (authpermissions.includes(constants.permissions.deleteRole)) {
      column.push({
        title: 'Delete',
        width: '6%',
        render: deleteColumn,
      });
    }

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1024, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Role"
        moduleLink="/roles/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={roles}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
      <DeleteModal
        hideDeleteModal={hideDeleteModal}
        roleDeleteModal={roleDeleteModal}
        roleListArray={roleListArray}
        deleteRoleId={deleteRoleId}
      />
    </>
  );
};

RoleList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(RoleList);
